import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Send, Loader2, Mail, Eye, StickyNote, AlertTriangle, CornerUpLeft, Sparkles, Trash2, X, Paperclip, FolderPlus, Video } from "lucide-react";
import { Avatar, Button, Input, Modal, Skeleton, Textarea, useToast } from "@/components/ui";
import { MAX_SMS_PARTS, describeForced, smsLength, tidyForSms } from "@/lib/sms-length";
import { uploadFile, makeUploadFilename } from "@/lib/upload";
import { ChannelIcon, getContactChannels } from "./ChannelPicker";
import { api, ApiError } from "@/lib/api";
import { stripHtmlForPreview } from "@/lib/html-preview";
import { SnippetPicker } from "@/features/snippets/SnippetPicker";
import { channelLabel, documentTitleFor, formatRelative } from "@/lib/format";
import { formatSender, syncsuiteEmailSettingsUrl, useEmailSending, type EmailSendingConfig } from "@/lib/email-sending";
import { useEmailSubjects } from "@/lib/email-subjects";
import type { ClientDocument, Contact, Conversation, Message, MessageAttachment } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Unified chat: every contact has exactly one thread. Inbound messages
 * from any channel (SMS, email, IG DM, WhatsApp, etc.) interleave
 * chronologically. The composer has a channel chip row that decides
 * what provider gets the next outbound — the conversation itself is
 * channel-agnostic.
 *
 * `conversationId` is null when the contact has no thread yet; the
 * first send creates it server-side and `onCreated` flips the parent
 * into "have a thread" mode.
 */
/**
 * What she had typed and not sent, kept for her.
 *
 *   "if we started a email or text and have to leave the chart for any reason
 *    can it save what we put in the box not send just save so when we get back
 *    to it it's still there"
 *
 * Leaving a chart clears the composer, which is right for the thread and wrong
 * for the half-written message: a client calls, the desk opens their file, and
 * the message that was being written for somebody else is gone. Kept per
 * client in this browser, exactly where the composer's last channel choice
 * already lives, and thrown away the moment it is sent.
 *
 * Every read and write is wrapped: storage can be full, blocked, or turned off
 * for the browser, and none of that is a reason the composer should stop
 * working.
 */
interface ComposerDraft { body: string; subject: string; channel: string; at: number }

/** A draft nobody came back to in a fortnight is not a draft any more. */
const DRAFT_KEEP_MS = 14 * 24 * 60 * 60 * 1000;

function draftKey(contactId: string): string {
  return `crm.contact.${contactId}.draft`;
}

function readDraft(contactId: string): ComposerDraft | null {
  try {
    const raw = window.localStorage.getItem(draftKey(contactId));
    if (!raw) return null;
    const d = JSON.parse(raw) as Partial<ComposerDraft>;
    if (typeof d?.body !== "string" && typeof d?.subject !== "string") return null;
    if (typeof d.at === "number" && Date.now() - d.at > DRAFT_KEEP_MS) {
      window.localStorage.removeItem(draftKey(contactId));
      return null;
    }
    return { body: d.body ?? "", subject: d.subject ?? "", channel: d.channel ?? "", at: d.at ?? 0 };
  } catch { return null; }
}

function writeDraft(contactId: string, d: Omit<ComposerDraft, "at">): void {
  try {
    if (!d.body.trim() && !d.subject.trim()) { window.localStorage.removeItem(draftKey(contactId)); return; }
    window.localStorage.setItem(draftKey(contactId), JSON.stringify({ ...d, at: Date.now() }));
  } catch { /* storage full or switched off - the composer still works */ }
}

function clearDraft(contactId: string): void {
  try { window.localStorage.removeItem(draftKey(contactId)); } catch { /* ok */ }
}

export function ConversationView({
  contact,
  conversationId,
  onCreated,
  onAfterSend,
  emptyMessage,
  autoFocus = true,
}: {
  contact: Contact;
  conversationId: string | null;
  onCreated?: (conversationId: string) => void;
  onAfterSend?: () => void;
  emptyMessage?: string;
  /**
   * Focus the composer on mount. True in the Inbox, where you arrived to
   * type. FALSE on a client's record: focusing here scrolls the panel's
   * scroll container to bring the composer into view, which drags the
   * client's name and tabs off the top of the screen.
   */
  autoFocus?: boolean;
}) {
  const toast = useToast();
  const [active, setActive] = useState<{ conversation: Conversation; messages: Message[] } | null>(null);
  const [body, setBody] = useState(() => readDraft(contact.id)?.body ?? "");
  /** Shown for a moment after a draft is put back, so she knows why text is there. */
  const [restored, setRestored] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [feedFilter, setFeedFilter] = useState<"all" | "notes" | "sms" | "email">("all");
  const [noteStripDismissed, setNoteStripDismissed] = useState(false);
  const deleteNote = async (m: Message) => {
    try {
      await api.delete(`/conversations/${m.conversation_id}/messages/${m.id}`);
      setActive((prev) => prev ? { ...prev, messages: prev.messages.filter((x) => x.id !== m.id) } : prev);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't delete the note");
    }
  };
  /* "Is the polish an AI type so it can answer based on the conversation?"
     This one reads the thread and writes the reply into the box; nothing is
     sent until she presses send. */
  const draftReply = async () => {
    if (!conversationId) return;
    setRewriting(true);
    try {
      const r = await api.post<{ text: string }>("/ai/reply", {
        conversation_id: conversationId,
        channel: composerChannel === "email" ? "email" : "sms",
      });
      setBody((prev) => (prev.trim() ? `${prev.trim()}\n\n${r.text}` : r.text));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Writing help is unavailable right now");
    } finally {
      setRewriting(false);
    }
  };

  const rewrite = async (instruction: string) => {
    if (!body.trim()) return;
    setRewriting(true);
    try {
      const r = await api.post<{ text: string }>("/ai/rewrite", {
        text: body,
        instruction,
        channel: composerChannel === "email" ? "email" : "sms",
      });
      setBody(r.text);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Writing help is unavailable right now");
    } finally {
      setRewriting(false);
    }
  };
  const [subject, setSubject] = useState(() => readDraft(contact.id)?.subject ?? "");
  const [sending, setSending] = useState(false);

  /* Land on the NEWEST message, every time.
   *
   * Her video: clicking between clients dropped the feed at the oldest
   * message and she scrolled down on every single one. Calling
   * scrollIntoView once after the fetch wasn't enough — avatars, images and
   * a long email body all settle AFTER that first paint, and each one
   * pushed the bottom further down while the view stayed where it was.
   *
   * So the feed is pinned to the bottom instead of scrolled once: a resize
   * observer re-pins on every late layout change, and the pin releases the
   * moment she scrolls up to read history (a new message then no longer
   * yanks the feed away from her). Switching conversations pins again. */
  const feedRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(true);
  const stickToBottom = useCallback((smooth = false) => {
    const el = feedRef.current;
    if (!el || !pinnedRef.current) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, []);
  const onFeedScroll = () => {
    const el = feedRef.current;
    if (!el) return;
    // 40px of slack: "near enough the bottom" still counts as following.
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };
  useLayoutEffect(() => {
    const el = feedRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => stickToBottom());
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    return () => ro.disconnect();
  });

  // Channels we can reach the contact on. Internal-note is always
  // available regardless of identities. The selected channel persists
  // per contact via localStorage so the next message defaults to
  // whatever the user picked last time.
  const readyChannels = useMemo(() => getContactChannels(contact).filter((c) => c.ready), [contact]);

  const initialChannel = useMemo(() => {
    /**
     * Text first, always.
     *
     * "can we make txt (sms) the default one in the messages and pt charts so
     *  it always defaults to txt as opposed to email?"
     *
     * It used to open on whichever identity came back first, which for a
     * client with an email on file was often email - so a quick reply to a
     * text went out as a message they would not see until later. A mobile on
     * file now wins outright; her own last choice for that person is only
     * consulted when there is no mobile to text.
     */
    const realChannels = readyChannels.filter((c) => c.channel !== "internal_note");
    if (realChannels.some((c) => c.channel === "sms")) return "sms";
    const stored = typeof window !== "undefined"
      ? window.localStorage.getItem(`crm.contact.${contact.id}.lastChannel`)
      : null;
    if (stored && readyChannels.some((c) => c.channel === stored)) return stored;
    return realChannels[0]?.channel ?? readyChannels[0]?.channel ?? "internal_note";
  }, [contact.id, readyChannels]);

  const [composerChannel, setComposerChannel] = useState<string>(initialChannel);
  useEffect(() => { setComposerChannel(initialChannel); }, [initialChannel]);

  // Sending identity for the From line above the composer. Only fetched
  // once the user is actually on the email channel — no reason to hit the
  // endpoint for an SMS-only contact.
  const { config: sendingConfig } = useEmailSending(composerChannel === "email");
  const subjectOptions = useEmailSubjects();

  // Load + poll the active conversation.
  //
  // Identity gates throughout: a response is only adopted if its
  // conversation.id matches the prop AND the contact_id matches our
  // contact.id. This is the defensive layer against the "wrong messages
  // briefly showing under the wrong contact" class of bug — even a
  // misrouted backend response or a slow-resolving stale fetch can't
  // leak another conversation's content into the UI.
  // Toast accessed via ref so this effect's deps don't include a context
  // value (defensive — if any context provider above us forgets to
  // memoize its value, our chat shouldn't reload on every render).
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // The conversation + contact this component is CURRENTLY showing. Async
  // work started before a switch reads these on completion instead of the
  // values it captured when it started, so a reply that lands after the
  // user has moved on can't paint itself into the new thread. The load and
  // poll effects have their own `cancelled` flags; this covers `send`,
  // which outlives its effect.
  const viewingRef = useRef({ conversationId, contactId: contact.id });
  viewingRef.current = { conversationId, contactId: contact.id };
  /** Did the view stay put? Pass the ids captured when the work started. */
  const isStillViewing = (convId: string | null, contactId: string) =>
    viewingRef.current.conversationId === convId &&
    viewingRef.current.contactId === contactId;

  useEffect(() => {
    pinnedRef.current = true;
    setActive(null);
    /* Not cleared: whatever was half-written for THIS client comes back,
       and an empty draft reads as an empty box exactly as before. */
    const saved = readDraft(contact.id);
    setBody(saved?.body ?? "");
    setSubject(saved?.subject ?? "");
    setRestored(Boolean(saved?.body?.trim() || saved?.subject?.trim()));
    // A send in flight for the previous thread no longer owns this
    // composer — re-enable it immediately rather than waiting on a
    // request that will be gated out anyway.
    setSending(false);
    if (!conversationId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<{ conversation: Conversation; messages: Message[] }>(`/conversations/${conversationId}`);
        if (cancelled) return;
        if (data.conversation.id !== conversationId) return;
        if (data.conversation.contact_id !== contact.id) return;
        // Drop any message whose conversation_id doesn't match — server
        // shouldn't return any, but if it ever does we won't render it.
        const safeMessages = data.messages.filter(
          (m) => m.conversation_id === conversationId
        );
        setActive({ conversation: data.conversation, messages: safeMessages });
        pinnedRef.current = true;
        stickToBottom();
        // Late layout (avatars, an email body) lands after this paint.
        setTimeout(() => stickToBottom(), 0);
        if (data.conversation.unread_count > 0) {
          void api.patch(`/conversations/${conversationId}`, { mark_read: true });
        }
      } catch (err) {
        if (!cancelled) toastRef.current.error(err instanceof ApiError ? err.message : "Failed to load conversation");
      }
    })();
    return () => { cancelled = true; };
  }, [conversationId, contact.id]);

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled || document.hidden) return;
      try {
        const data = await api.get<{ conversation: Conversation; messages: Message[] }>(`/conversations/${conversationId}`);
        if (cancelled) return;
        if (data.conversation.id !== conversationId) return;
        if (data.conversation.contact_id !== contact.id) return;
        const safeMessages = data.messages.filter(
          (m) => m.conversation_id === conversationId
        );
        setActive((prev) => {
          if (!prev) return { conversation: data.conversation, messages: safeMessages };
          // Merge polled data into the existing state by preserving
          // object references for unchanged rows. React's reconciliation
          // skips re-rendering a child when its props are
          // referentially-equal to the previous render — so message
          // bubbles, avatars, and the right rail's chips/timeline don't
          // flash on every 5-second tick. The ONLY rows that get a new
          // reference are the ones whose content actually changed (a
          // queued message transitioning to delivered, a new inbound
          // arriving, etc).
          const prevById = new Map(prev.messages.map((m) => [m.id, m]));
          let changed = prev.messages.length !== safeMessages.length;
          const mergedMessages = safeMessages.map((m, i) => {
            const old = prevById.get(m.id);
            if (!old) { changed = true; return m; }
            // Same id → check if any displayed field actually moved.
            const samePos = prev.messages[i]?.id === m.id;
            const sameStatus = old.status === m.status;
            const sameBody = old.body === m.body;
            const sameDelivered = old.delivered_at === m.delivered_at;
            const sameRead = old.read_at === m.read_at;
            const sameError = old.error === m.error;
            if (samePos && sameStatus && sameBody && sameDelivered && sameRead && sameError) {
              return old; // identical → keep old reference, no re-render
            }
            changed = true;
            return m;
          });
          // Optimistic-message reconciliation: any tmp_* row in prev
          // that now has a real server twin (matched by body+timestamp
          // proximity is fragile, so just keep tmp rows until the next
          // poll picks up their server-issued id naturally).
          const sameUnread = prev.conversation.unread_count === data.conversation.unread_count;
          if (!changed && sameUnread) return prev;
          return {
            // Only mint a new conversation reference if its displayed
            // bits moved — keeps subject/header stable across polls.
            conversation: sameUnread &&
              prev.conversation.subject === data.conversation.subject &&
              prev.conversation.status === data.conversation.status
              ? prev.conversation
              : data.conversation,
            messages: changed ? mergedMessages : prev.messages,
          };
        });
      } catch { /* transient */ }
    };
    const timer = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [conversationId, contact.id]);

  /**
   * Saved as she types, a beat behind the keystrokes.
   *
   * The delay is only there to keep a long message from writing to storage on
   * every letter; a second of typing is never lost, because leaving the page
   * does not cancel a timer that has already fired, and the unmount below
   * writes whatever the last keystroke left.
   */
  useEffect(() => {
    const t = setTimeout(() => writeDraft(contact.id, { body, subject, channel: composerChannel }), 400);
    return () => clearTimeout(t);
  }, [contact.id, body, subject, composerChannel]);

  /* Closing the tab, or the panel, mid-sentence. The timer above may not have
     fired yet, so the last state is written out on the way past. */
  const leavingRef = useRef({ contactId: contact.id, body, subject, channel: composerChannel });
  leavingRef.current = { contactId: contact.id, body, subject, channel: composerChannel };
  useEffect(() => {
    const flush = () => {
      const l = leavingRef.current;
      writeDraft(l.contactId, { body: l.body, subject: l.subject, channel: l.channel });
    };
    window.addEventListener("pagehide", flush);
    return () => { window.removeEventListener("pagehide", flush); flush(); };
  }, []);

  const send = async () => {
    if (!body.trim()) return;
    const sentBody = body;
    const sentSubject = subject;
    const channel = composerChannel;

    // Persist the user's last channel choice so the next outbound
    // for this contact defaults to it.
    try { window.localStorage.setItem(`crm.contact.${contact.id}.lastChannel`, channel); } catch { /* ok */ }
    // It has gone: there is nothing left to come back to.
    clearDraft(contact.id);
    setRestored(false);

    // Ids this send belongs to. Everything below is gated on the view
    // still showing them — switching threads mid-send must not splice this
    // thread's result into another one.
    const sentConversationId = conversationId;
    const sentContactId = contact.id;

    if (sentConversationId && active) {
      // Append to the existing thread.
      setSending(true);
      const optimistic: Message = {
        id: `tmp_${Date.now()}`,
        conversation_id: sentConversationId,
        contact_id: active.conversation.contact_id,
        direction: "outbound",
        channel,
        status: "queued",
        body: sentBody,
        subject: sentSubject || null,
        attachments: [], external_id: null,
        author_type: "user", author_id: null,
        from_address: null, from_name: null,
        error: null,
        sent_at: null, delivered_at: null, read_at: null,
        created_at: new Date().toISOString(),
      };
      // Functional update — `active` from the closure can already be a
      // render behind if a poll tick landed between keystroke and click.
      setActive((prev) => (prev ? { ...prev, messages: [...prev.messages, optimistic] } : prev));
      setBody(""); setSubject("");
      // Sending always brings her back down, even if she was reading history.
      pinnedRef.current = true;
      setTimeout(() => stickToBottom(true), 0);
      try {
        await api.post(`/conversations/${sentConversationId}/messages`, {
          channel,
          body: sentBody,
          subject: sentSubject || undefined,
        });
        const data = await api.get<{ conversation: Conversation; messages: Message[] }>(`/conversations/${sentConversationId}`);
        // Same identity gates the load/poll paths use, plus the "are we
        // still here" check — the send survives unmount of the effects.
        if (!isStillViewing(sentConversationId, sentContactId)) return;
        if (data.conversation.id !== sentConversationId) return;
        if (data.conversation.contact_id !== sentContactId) return;
        setActive({
          conversation: data.conversation,
          messages: data.messages.filter((m) => m.conversation_id === sentConversationId),
        });
        onAfterSend?.();
      } catch (err) {
        // A failure still belongs to the thread it was typed in — only
        // surface it (and mark the bubble) if that thread is on screen.
        if (!isStillViewing(sentConversationId, sentContactId)) return;
        toast.error(err instanceof ApiError ? err.message : "Send failed");
        setActive((prev) => prev ? { ...prev, messages: prev.messages.map(m => m.id === optimistic.id ? { ...m, status: "failed", error: "Send failed" } : m) } : prev);
      } finally {
        // Always clear — `sending` is a composer-local flag, and leaving
        // it set after a thread switch would disable the new composer.
        setSending(false);
      }
      return;
    }

    // No thread yet — create it via the upsert route.
    setSending(true);
    setBody(""); setSubject("");
    try {
      const r = await api.post<{ conversation: { id: string }; error: string | null }>("/conversations", {
        contact_id: sentContactId,
        channel,
        subject: sentSubject || undefined,
        body: sentBody,
      });
      // The thread was created for THIS contact — if the user has since
      // moved to another one, don't hand the parent a conversation id
      // belonging to a contact it's no longer showing.
      if (!isStillViewing(null, sentContactId)) return;
      if (r.error) toast.error(r.error);
      onCreated?.(r.conversation.id);
      onAfterSend?.();
    } catch (err) {
      if (!isStillViewing(null, sentContactId)) return;
      toast.error(err instanceof ApiError ? err.message : "Send failed");
      setBody(sentBody); setSubject(sentSubject);
      // The words are back in the box, so they are a draft again.
      writeDraft(sentContactId, { body: sentBody, subject: sentSubject, channel });
    } finally {
      setSending(false);
    }
  };

  if (!conversationId && readyChannels.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center text-center p-8">
        <p className="text-sm text-zinc-500">{emptyMessage ?? "Pick a conversation to read or reply"}</p>
      </div>
    );
  }

  if (conversationId && !active) {
    return <MessagesSkeleton />;
  }

  const isInternalNote = composerChannel === "internal_note";
  const channelName = channelLabel(composerChannel);

  // "Important internal team notes get buried as new customer texts come in."
  // Two answers, both hers: a feed filter, and the newest note pinned above
  // the composer so it never scrolls away.
  const visibleMessages = (active?.messages ?? []).filter((m) =>
    feedFilter === "all" ? true
    : feedFilter === "notes" ? m.channel === "internal_note"
    : m.channel === feedFilter);
  const internalNotes = (active?.messages ?? []).filter((m) => m.channel === "internal_note");
  const latestNote = internalNotes[internalNotes.length - 1];

  return (
    <>
      {(active?.messages.length ?? 0) > 0 && (
        <div className="flex shrink-0 items-center gap-1 border-b border-zinc-800/70 px-3 py-1.5">
          {([["all", "All"], ["notes", "Notes"], ["sms", "Texts"], ["email", "Email"]] as const).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFeedFilter(k)}
              className={cn(
                "rounded-md px-2 py-0.5 text-[11px] font-medium",
                feedFilter === k ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-200",
              )}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      <div ref={feedRef} onScroll={onFeedScroll} className="flex-1 overflow-y-auto p-4 space-y-3">
        {!active || active.messages.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-12">
            {conversationId ? "No messages yet — say hi 👋" : `Start a conversation — first message goes via ${channelName}.`}
          </p>
        ) : visibleMessages.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-12">Nothing on this filter yet.</p>
        ) : visibleMessages.map((m) => (
          <MessageBubble key={m.id} message={m} contactName={contact.first_name ?? contact.last_name ?? null} contactAvatar={contact.avatar_url} onDeleteNote={deleteNote} />
        ))}
      </div>

      {/* The newest team note stays docked above the composer — visible no
          matter how far the client conversation has scrolled past it. */}
      {latestNote && feedFilter !== "notes" && !noteStripDismissed && (
        <div className="flex shrink-0 items-start gap-2 border-t border-amber-500/30 bg-amber-500/10 px-3 py-2">
          <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
          <p className="min-w-0 flex-1 truncate text-xs text-amber-200" title={latestNote.body ?? ""}>
            {latestNote.body}
            {internalNotes.length > 1 && (
              <button className="ml-1.5 text-amber-400 underline-offset-2 hover:underline" onClick={() => setFeedFilter("notes")}>
                +{internalNotes.length - 1} more
              </button>
            )}
          </p>
          <button className="shrink-0 text-amber-500/70 hover:text-amber-300" title="Hide" onClick={() => setNoteStripDismissed(true)}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <div className={cn(
        "border-t p-3 shrink-0 transition-colors",
        isInternalNote ? "border-amber-500/30 bg-amber-500/5" : "border-zinc-800/70 bg-zinc-950",
      )}>
        {/* Channel picker — every reachable channel for this contact,
            plus internal_note. Selecting just changes what provider the
            next message routes to; the thread itself is channel-agnostic. */}
        {readyChannels.length > 1 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {readyChannels.map((c) => (
              <button
                key={c.channel}
                type="button"
                onClick={() => setComposerChannel(c.channel)}
                title={c.hint}
                className={cn(
                  "inline-flex items-center gap-1 rounded-md px-2 h-7 text-xs font-medium transition-colors",
                  composerChannel === c.channel
                    ? c.channel === "internal_note"
                      ? "bg-amber-500/30 text-amber-100"
                      : "bg-indigo-500 text-white"
                    : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700",
                )}
              >
                <c.icon className="h-3 w-3" />
                {c.label}
              </button>
            ))}
          </div>
        )}
        {composerChannel === "email" && (
          <>
            <ComposerSenderLine config={sendingConfig} />
            {/* A datalist, not a <select>: her four are there in one click
                and a one-off subject can still just be typed. */}
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              list="email-subject-options"
              placeholder={active?.conversation.subject ? `Re: ${active.conversation.subject}` : "Subject"}
              className="h-8 text-xs mb-2"
            />
            <datalist id="email-subject-options">
              {subjectOptions.map((opt) => <option key={opt} value={opt} />)}
            </datalist>
          </>
        )}
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={isInternalNote
            ? "Internal note — only visible to your team"
            : `Message via ${channelName}…`}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") send(); }}
          className={cn("min-h-12", isInternalNote && "bg-amber-500/5")}
          autoFocus={autoFocus}
        />
        {/* What this text will actually cost, before she sends it. She hit
            the carrier's limit with no warning: "Message too large - The SMS
            message would be divided into 13 parts. The maximum is 10." */}
        {composerChannel === "sms" && body.trim() !== "" && <SmsMeter body={body} />}
        <div className="mt-2 flex items-center justify-between">
          <span className={cn(
            "text-[10px] inline-flex items-center gap-1",
            isInternalNote ? "text-amber-400" : "text-zinc-500",
          )}>
            <ChannelIcon channel={composerChannel} /> {channelName} · ⌘+Enter to {isInternalNote ? "add" : "send"}
            {/* Said once, when it matters: she left the chart and the words
                are still here. It is not a running "saving…" label, which
                would be noise on every keystroke. */}
            {restored && (body.trim() || subject.trim()) && (
              <span className="text-emerald-400">· picked up where you left off</span>
            )}
          </span>
          <div className="flex items-center gap-1.5">
          {/* Writing help — "the corrections are there and we can ask chat to
              say it in a certain way". Rewrites the draft in place; nothing
              sends until she hits Send herself. Runs on the platform's own
              AI — no outside account needed. */}
          {/* Saved replies — "prepared sample responses … placed into the
              response", plus her Google review link. Filled in for THIS
              client on insert (the send path doesn't resolve fill-ins), so
              the box shows exactly what will go out. */}
          {!isInternalNote && (
            <SnippetPicker
              channel={composerChannel === "email" ? "email" : "sms"}
              contact={contact}
              placement="up"
              onInsert={(text) => setBody((prev) => (prev ? prev + "\n" + text : text))}
            />
          )}
          {!isInternalNote && (
            <FilePicker
              contactId={contact.id}
              onInsert={(text) => setBody((prev) => (prev.trim() ? `${prev.trim()}\n${text}` : text))}
            />
          )}
          {/* "I need a way to resend the link for the remote consultation."
              Only on a client who has one coming: a button that is there for
              everybody is a button nobody trusts. */}
          {!isInternalNote && (
            <VideoLinkButton
              contactId={contact.id}
              onInsert={(text) => {
                setBody((prev) => (prev.trim() ? `${prev.trim()}\n${text}` : text));
                setRestored(false);
              }}
            />
          )}
          {!isInternalNote && (
            <RewriteMenu
              // Polishing needs a draft; writing one only needs a thread.
              disabled={rewriting || (!body.trim() && !conversationId)}
              rewriting={rewriting}
              hasDraft={!!body.trim()}
              canDraft={!!conversationId}
              onPick={(instruction) => void rewrite(instruction)}
              onDraftReply={() => void draftReply()}
            />
          )}
          <Button
            onClick={send}
            loading={sending}
            disabled={!body.trim() || (composerChannel === "sms" && smsLength(tidyForSms(body)).tooLong)}
            size="sm"
            variant={isInternalNote ? "outline" : "primary"}
            leftIcon={isInternalNote ? <StickyNote className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
          >
            {isInternalNote ? "Add note" : "Send"}
          </Button>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Send a file.
 *
 * The mail service takes no attachments, so a file travels as a link: pick
 * one off the client's chart (or upload a new one), and its name and link
 * drop into the message. The link is a token this app controls — it opens a
 * fresh signed download each time and can be switched off later — so nothing
 * permanent about the file ends up sitting in someone's inbox.
 */
/**
 * Put the video consultation link back in the box.
 *
 *   "I need a way to resend the link for the remote consultation. Is there a
 *    way you can add a resend remote link button but the email txt area?"
 *
 * It goes out on its own a few minutes before the visit. This is for the
 * client who says they never got it: one press puts the same message in the
 * composer, she reads it, and sends it like anything else. Nothing is sent
 * from here and nothing is marked as sent, so the automatic one still goes.
 *
 * The button only exists for a client with a video visit still ahead of them.
 * It says which visit, so it cannot be pressed for the wrong one.
 */
function VideoLinkButton({
  contactId, onInsert,
}: {
  contactId: string;
  onInsert: (text: string) => void;
}) {
  const toast = useToast();
  const [state, setState] = useState<
    { link: { starts_at: string; message: string; provider_name: string | null } | null; reason?: string } | null
  >(null);

  useEffect(() => {
    let cancelled = false;
    setState(null);
    api.get<{ link: { starts_at: string; message: string; provider_name: string | null } | null; reason?: string }>(
      `/frontdesk/clients/${contactId}/video-link`)
      .then((d) => { if (!cancelled) setState(d); })
      .catch(() => { if (!cancelled) setState({ link: null, reason: "failed" }); });
    return () => { cancelled = true; };
  }, [contactId]);

  // No video visit coming: no button. A provider with no room on file is the
  // one case worth saying out loud, because it is fixable and it is also why
  // the automatic link never went either.
  if (!state) return null;
  if (!state.link) {
    if (state.reason !== "no_room_on_provider") return null;
    return (
      <span
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-amber-400"
        title="Their video visit has no room on the provider's record, so no link can be sent. Add one under Manage, Team."
      >
        <Video className="h-3.5 w-3.5" />
        No video room
      </span>
    );
  }

  const when = new Date(state.link.starts_at).toLocaleString(undefined,
    { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return (
    <button
      type="button"
      onClick={() => {
        onInsert(state.link!.message);
        toast.success("Video link added", "Read it over, then press Send.");
      }}
      title={`The join link for their video consultation on ${when}`}
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
    >
      <Video className="h-3.5 w-3.5" />
      Video link
    </button>
  );
}

function FilePicker({
  contactId, onInsert,
}: {
  contactId: string;
  onInsert: (text: string) => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState<ClientDocument[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    setDocs(null);
    api.get<{ documents: ClientDocument[] }>("/documents", { contact_id: contactId })
      .then((d) => setDocs(d.documents))
      .catch(() => setDocs([]));
  }, [contactId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const insert = async (doc: ClientDocument) => {
    setBusyId(doc.id);
    try {
      const r = await api.post<{ url: string; title: string }>(`/documents/${doc.id}/share`, {});
      onInsert(`${doc.title}: ${r.url}`);
      setOpen(false);
      toast.success("Link added", "It opens the file for 30 days, and you can turn it off any time.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't make a link for that file");
    } finally {
      setBusyId(null);
    }
  };

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const up = await uploadFile(file, {
        path: `documents/${contactId}/${makeUploadFilename(file.name)}`,
        bucket: "private",
      });
      const created = await api.post<{ document: ClientDocument }>("/documents", {
        contact_id: contactId,
        title: documentTitleFor(file.name),
        category: "other",
        file_url: up.key,
        content_type: file.type || null,
        size_bytes: file.size,
      });
      await insert(created.document);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't upload that");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} leftIcon={<Paperclip className="h-3.5 w-3.5" />}>
        File
      </Button>
      {open && (
        <Modal open onClose={() => setOpen(false)} title="Send a file" size="md">
          <p className="mb-3 text-xs text-zinc-500">
            The file goes as a link they can open for 30 days. Nothing leaves the chart.
          </p>
          <div className="mb-3">
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
            />
            <Button size="sm" variant="secondary" loading={uploading} onClick={() => fileRef.current?.click()}>
              Upload a new file
            </Button>
          </div>
          {docs === null ? (
            <Skeleton className="h-24 w-full" />
          ) : docs.length === 0 ? (
            <p className="py-6 text-center text-sm text-zinc-500">Nothing on this client's file yet.</p>
          ) : (
            <ul className="max-h-72 divide-y divide-zinc-800/60 overflow-y-auto">
              {docs.map((d) => (
                <li key={d.id} className="flex items-center gap-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-zinc-200">{d.title}</p>
                    <p className="text-[11px] text-zinc-500">{d.category.replace(/_/g, " ")}</p>
                  </div>
                  <Button size="sm" variant="outline" loading={busyId === d.id} onClick={() => void insert(d)}>
                    Add link
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </>
  );
}

/** ✨ menu: fix or restyle the draft. Verbs she used on the phone. */
function RewriteMenu({
  disabled, rewriting, hasDraft, canDraft, onPick, onDraftReply,
}: {
  disabled: boolean;
  rewriting: boolean;
  /** Is there anything in the box to polish? */
  hasDraft: boolean;
  /** Is there a conversation to answer? */
  canDraft: boolean;
  onPick: (instruction: string) => void;
  onDraftReply: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const OPTIONS: Array<[string, string]> = [
    ["fix", "Fix spelling & grammar"],
    ["friendlier and warmer", "Friendlier"],
    ["shorter and to the point", "Shorter"],
    ["more professional and formal", "More professional"],
    ["simpler, plain English a client will understand at a glance", "Simpler"],
  ];
  return (
    <div className="relative" ref={ref}>
      <Button
        size="sm"
        variant="outline"
        disabled={disabled}
        loading={rewriting}
        onClick={() => setOpen((o) => !o)}
        leftIcon={<Sparkles className="h-3.5 w-3.5" />}
      >
        Polish
      </Button>
      {open && (
        <div className="absolute bottom-full right-0 z-30 mb-1 w-56 overflow-hidden rounded-lg border border-zinc-700 bg-zinc-900 py-1 shadow-xl">
          {canDraft && (
            <>
              <button
                type="button"
                data-draft-reply
                className="block w-full px-3 py-1.5 text-left text-xs font-medium text-indigo-200 hover:bg-zinc-800"
                onClick={() => { setOpen(false); onDraftReply(); }}
              >
                Write a reply for me
                <span className="block text-[10px] font-normal text-zinc-500">Reads this conversation. Nothing sends.</span>
              </button>
              <div className="my-1 border-t border-zinc-800" />
            </>
          )}
          {OPTIONS.map(([instruction, label]) => (
            <button
              key={instruction}
              type="button"
              disabled={!hasDraft}
              className="block w-full px-3 py-1.5 text-left text-xs text-zinc-200 hover:bg-zinc-800 disabled:opacity-40"
              onClick={() => { setOpen(false); onPick(instruction); }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * "From: …" line above the email composer. The whole point of this
 * feature: nobody should have to send an email to discover what address
 * it went out as. Three states —
 *
 *   - sendable  → the exact From (and Reply-To, when it differs)
 *   - blocked   → no verified domain, or no mailing address on file in
 *                 SyncSuite, with the fix one click away
 *   - unknown   → config fetch failed; stay silent rather than guess
 *
 * The address shown comes from the same resolver the send uses, so this
 * can't drift from reality.
 */
function ComposerSenderLine({ config }: { config: EmailSendingConfig | null }) {
  // `known: false` = the platform lookup failed. Show nothing rather than
  // claim a setup problem the user doesn't have.
  if (!config || !config.known) return null;

  const settingsUrl = syncsuiteEmailSettingsUrl();

  if (!config.can_send) {
    // Two different blockers, two different fixes — say which one it is
    // rather than sending someone to re-check a domain that's already
    // verified. A missing domain is the more fundamental of the two, so
    // it wins when both are missing.
    const noDomain = config.verified_domains.length === 0;
    return (
      <div className="mb-2 flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-200">
        <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
        <span>
          {noDomain
            ? "No verified sending domain yet — email sends will fail. "
            : "No business mailing address on file — email sends will fail. It isn't shown in personal email, but it's required before anything sends. "}
          {settingsUrl ? (
            <a href={settingsUrl} target="_blank" rel="noreferrer" className="font-medium underline hover:text-amber-100">
              {noDomain ? "Verify a domain in SyncSuite" : "Add your address in SyncSuite"}
            </a>
          ) : (
            <span className="font-medium">
              {noDomain
                ? "Verify one in SyncSuite → Integrations → Email."
                : "Add it in SyncSuite → Integrations → Email."}
            </span>
          )}
        </span>
      </div>
    );
  }

  const from = formatSender(config.effective.from_name, config.effective.from);
  if (!from) return null;

  return (
    <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md border border-zinc-800 bg-zinc-900/50 px-2 py-1.5 text-[11px]">
      <span className="font-medium uppercase tracking-wider text-zinc-500">From</span>
      <span className="text-zinc-200">{from}</span>
      {config.effective.reply_to && (
        <span className="inline-flex items-center gap-1 text-zinc-500">
          <CornerUpLeft className="h-3 w-3" />
          Replies go to {config.effective.reply_to}
        </span>
      )}
      {config.configured_domain_verified === false && (
        <span className="inline-flex items-center gap-1 text-amber-400">
          <AlertTriangle className="h-3 w-3" />
          {config.configured_domain} isn't verified — this send will fail
        </span>
      )}
    </div>
  );
}

/**
 * Skeleton row pattern for an unloaded chat thread. Alternates inbound /
 * outbound bubbles with realistic widths so the layout doesn't pop when
 * real messages slot in. Used both here (while messages fetch) and from
 * the inbox page (while the contact behind a freshly-clicked
 * conversation resolves) so the visual loading state is identical
 * across both transitions.
 */
export function MessagesSkeleton() {
  // Stable widths chosen to look like a normal short-thread tail: a
  // longer prompt, a short reply, a one-liner, a longer reply.
  const rows: Array<{ side: "in" | "out"; w: string }> = [
    { side: "in",  w: "w-48" },
    { side: "out", w: "w-32" },
    { side: "in",  w: "w-24" },
    { side: "out", w: "w-56" },
    { side: "in",  w: "w-40" },
    { side: "out", w: "w-28" },
  ];
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      {rows.map((r, i) => (
        <div
          key={i}
          className={cn(
            "flex gap-2",
            r.side === "out" && "flex-row-reverse"
          )}
        >
          {r.side === "in" && <Skeleton className="h-6 w-6 rounded-full shrink-0" />}
          <div className={cn("space-y-1", r.side === "out" && "items-end")}>
            <Skeleton className={cn("h-9 rounded-2xl", r.w)} />
            <Skeleton className="h-2.5 w-16 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Files that came in on a message.
 *
 * Her ask, verbatim: "if a patient emails lab work results from another MD
 * we need to get them from the email to the lab section in chart". So each
 * file opens in a tab, and "To chart" files it under the client's Labs
 * without anyone downloading and re-uploading anything — the chart document
 * points at the same stored file.
 */
const CHART_CATEGORIES: { value: string; label: string }[] = [
  { value: "lab_result", label: "Labs — result" },
  { value: "lab_script", label: "Labs — script" },
  { value: "form", label: "Forms" },
  { value: "medical_info", label: "Medical information" },
  { value: "recommendation", label: "Recommendation" },
  { value: "prior_notes", label: "Earlier notes" },
  { value: "insurance", label: "Insurance" },
  { value: "other", label: "Other document" },
];

function prettySize(bytes: number | null): string {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Attachments({ message }: { message: Message }) {
  const toast = useToast();
  const [menuFor, setMenuFor] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<Record<number, boolean>>({});
  const files = (message.attachments ?? []).filter((a): a is MessageAttachment => !!a && typeof a === "object");
  if (files.length === 0) return null;

  const open = async (a: MessageAttachment) => {
    if (a.url) { window.open(a.url, "_blank", "noopener"); return; }
    if (!a.key) return;
    try {
      const r = await api.get<{ url: string }>("/uploads/view-url", { key: a.key });
      window.open(r.url, "_blank", "noopener");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't open that file");
    }
  };

  const toChart = async (a: MessageAttachment, index: number, category: string) => {
    if (!a.key || !message.contact_id) return;
    setBusy(true);
    try {
      await api.post("/documents", {
        contact_id: message.contact_id,
        title: documentTitleFor(a.name),
        category,
        file_url: a.key,
        content_type: a.content_type,
        size_bytes: a.size_bytes,
        note: `Emailed in${message.from_name ? ` by ${message.from_name}` : ""}`,
      });
      setSaved((prev) => ({ ...prev, [index]: true }));
      toast.success("Filed in the chart", `${a.name} is under ${CHART_CATEGORIES.find((c) => c.value === category)?.label ?? category}.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't file that in the chart");
    } finally {
      setBusy(false);
      setMenuFor(null);
    }
  };

  return (
    <div className="mt-2 space-y-1" data-attachments>
      {files.map((a, i) => (
        <div key={`${a.name}-${i}`} className="relative flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => void open(a)}
            disabled={!a.key && !a.url}
            title={a.error ?? a.name}
            className={cn(
              "inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md border px-2 py-1 text-[11px]",
              a.key || a.url
                ? "border-zinc-700 text-zinc-200 hover:border-zinc-500 hover:text-white"
                : "border-zinc-800 text-zinc-500",
            )}
          >
            <Paperclip className="h-3 w-3 shrink-0" />
            <span className="truncate">{a.name}</span>
            {a.size_bytes ? <span className="shrink-0 text-zinc-500">{prettySize(a.size_bytes)}</span> : null}
          </button>
          {a.key && message.contact_id && (
            <button
              type="button"
              onClick={() => setMenuFor(menuFor === i ? null : i)}
              className={cn(
                "inline-flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-1 text-[11px]",
                saved[i]
                  ? "border-emerald-500/50 text-emerald-300"
                  : "border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white",
              )}
              title="Save this into the client's chart"
            >
              <FolderPlus className="h-3 w-3" />
              {saved[i] ? "In chart" : "To chart"}
            </button>
          )}
          {menuFor === i && (
            <div className="absolute left-0 top-full z-20 mt-1 w-44 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 py-1 shadow-xl shadow-black/40">
              {CHART_CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  disabled={busy}
                  onClick={() => void toChart(a, i, c.value)}
                  className="block w-full px-3 py-1.5 text-left text-xs text-zinc-200 hover:bg-zinc-800/60 disabled:opacity-50"
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}
          {a.error && <span className="text-[10px] text-amber-400">{a.error}</span>}
        </div>
      ))}
    </div>
  );
}

function emailHtmlFor(message: Message): string | null {
  const raw = message.raw_payload as { html?: unknown; body?: { html?: unknown } } | undefined;
  const fromRaw = typeof raw?.html === "string" ? raw.html
    : typeof raw?.body?.html === "string" ? raw.body.html
    : null;
  if (fromRaw && fromRaw.trim()) return fromRaw;
  if (message.body && message.body.trim().startsWith("<")) return message.body;
  return null;
}

function MessageBubble({ message, contactName, contactAvatar, onDeleteNote }: { message: Message; contactName?: string | null; contactAvatar?: string | null; onDeleteNote?: (m: Message) => void }) {
  const isOutbound = message.direction === "outbound";
  const isEmail = message.channel === "email";
  const isInternalNote = message.channel === "internal_note";
  const html = isEmail ? emailHtmlFor(message) : null;
  const [previewOpen, setPreviewOpen] = useState(false);
  // Long emails open in place; the reader window is for the styled version.
  const [expanded, setExpanded] = useState(false);
  const directionLabel = isOutbound ? "Sent via" : "Received via";
  const sender = isEmail ? formatSender(message.from_name, message.from_address) : "";

  if (isInternalNote) {
    return (
      <div className="flex gap-2">
        <div className="w-full max-w-[90%] mx-auto">
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100">
            <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-amber-300">
              <StickyNote className="h-3 w-3" />
              <span>Internal note</span>
              <span className="text-amber-500/50">·</span>
              <span className="normal-case tracking-normal text-amber-300/80">Only your team sees this</span>
              {onDeleteNote && (
                <button
                  onClick={() => onDeleteNote(message)}
                  className="ml-auto rounded p-0.5 text-amber-500/60 hover:bg-amber-500/20 hover:text-amber-200"
                  title="Delete this note (resolved)"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
            <p className="mt-1.5 whitespace-pre-wrap wrap-break-word text-amber-50/90">
              {message.body || <span className="opacity-60 italic">(empty)</span>}
            </p>
          </div>
          <p className="mt-1 text-[10px] text-amber-400/70 px-1">
            {formatRelative(message.sent_at ?? message.created_at)}
          </p>
        </div>
      </div>
    );
  }

  if (isEmail) {
    return (
      <>
        <div className={cn("flex gap-2", isOutbound && "flex-row-reverse")}>
          {!isOutbound && <Avatar name={contactName ?? "?"} src={contactAvatar} size="xs" />}
          <div className={cn("max-w-[85%] space-y-1", isOutbound && "items-end")}>
            <div className={cn(
              "rounded-xl border p-3 text-sm",
              isOutbound ? "border-indigo-500/30 bg-indigo-500/10" : "border-zinc-800 bg-zinc-900/60",
            )}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
                <Mail className="h-3 w-3" />
                <span>Email</span>
                <span className="text-zinc-600">·</span>
                <span className="capitalize">{message.status}</span>
              </div>
              {/* Exactly which identity this message used. Recorded at send
                  time, so historical messages keep showing the address they
                  actually went out as even after the workspace changes its
                  sender. */}
              {sender && (
                <p className="mt-1 text-[10px] text-zinc-500 truncate" title={sender}>
                  <span className="uppercase tracking-wider">From</span>{" "}
                  <span className="text-zinc-400">{sender}</span>
                </p>
              )}
              {message.subject && (
                <p className="mt-1 text-sm font-semibold text-zinc-100 wrap-break-word">{message.subject}</p>
              )}
              {/* "When I'm in the inbox page, I can't click to read the
                  whole email … The email says '...' after I send it to a
                  client." It was clamped to four lines with no way to open
                  it. Now the clamp lifts in place, and a plain-text email
                  gets the same full-window reader an HTML one has. */}
              <p
                data-email-body
                data-expanded={expanded ? "1" : "0"}
                className={cn(
                  "mt-1 text-xs text-zinc-300 whitespace-pre-wrap wrap-break-word",
                  !expanded && "line-clamp-4",
                )}
              >
                {message.body
                  ? (html ? stripHtmlForPreview(message.body) : message.body)
                  : <span className="opacity-60 italic">(empty)</span>}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                {(message.body ?? "").length > 180 && (
                  <button
                    type="button"
                    data-email-expand
                    onClick={() => setExpanded((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-indigo-300 hover:text-indigo-200"
                  >
                    {expanded ? "Show less" : "Read the whole email"}
                  </button>
                )}
                <button
                  type="button"
                  data-email-open
                  onClick={() => setPreviewOpen(true)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-indigo-300 hover:text-indigo-200"
                >
                  <Eye className="h-3 w-3" /> {html ? "View styled email" : "Open in a window"}
                </button>
              </div>
              <Attachments message={message} />
            </div>
            <p className={cn("text-[10px] text-zinc-500 px-1 inline-flex items-center gap-1", isOutbound && "justify-end w-full")}>
              {message.status === "queued" && <Loader2 className="inline h-2.5 w-2.5 animate-spin" />}
              <Mail className="h-2.5 w-2.5" />
              <span>{directionLabel} email</span>
              <span className="text-zinc-600">·</span>
              <span>{formatRelative(message.sent_at ?? message.created_at)}</span>
              {message.status === "failed" && <span className="text-rose-400">· {message.error || "failed"}</span>}
              {isOutbound && message.delivered_at && message.status !== "failed" && (
                <span className="text-emerald-400/80">· delivered</span>
              )}
            </p>
          </div>
        </div>
        {previewOpen && (
          <EmailPreviewModal
            subject={message.subject}
            html={html ?? plainAsHtml(message.body ?? "")}
            onClose={() => setPreviewOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <div className={cn("flex gap-2", isOutbound && "flex-row-reverse")}>
      {!isOutbound && <Avatar name={contactName ?? "?"} src={contactAvatar} size="xs" />}
      <div className={cn("max-w-[80%] space-y-0.5", isOutbound && "items-end")}>
        {message.subject && (
          <p className={cn("text-xs font-semibold", isOutbound ? "text-zinc-300 text-right" : "text-zinc-300")}>{message.subject}</p>
        )}
        <div className={cn(
          "rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap wrap-break-word",
          isOutbound ? "bg-indigo-600 text-white" : "bg-zinc-800 text-zinc-200"
        )}>
          {message.body || <span className="opacity-60 italic">(empty)</span>}
          <Attachments message={message} />
        </div>
        {/* Provenance line: every message shows which channel it went
            through, so a unified thread mixing SMS / email / IG / etc.
            is always readable at a glance. */}
        <p className={cn("text-[10px] text-zinc-500 px-1 inline-flex items-center gap-1 flex-wrap", isOutbound && "justify-end w-full")}>
          {message.status === "queued" && <Loader2 className="inline h-2.5 w-2.5 animate-spin" />}
          <ChannelIcon channel={message.channel} />
          <span>{directionLabel} {channelLabel(message.channel)}</span>
          <span className="text-zinc-600">·</span>
          <span>{formatRelative(message.sent_at ?? message.created_at)}</span>
          {message.status === "failed" && <span className="text-rose-400">· {message.error || "failed"}</span>}
          {/* Now that a delivery receipt can actually land, say so: she is
              texting clients all day and "did that go?" was unanswerable. */}
          {isOutbound && message.delivered_at && message.status !== "failed" && (
            <span className="text-emerald-400/80">· delivered</span>
          )}
        </p>
      </div>
    </div>
  );
}

/** A plain-text email, wrapped just enough to open in the reader window. */
function plainAsHtml(body: string): string {
  const escaped = body
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return `<!doctype html><meta charset="utf-8"><body style="margin:0;padding:24px;font:14px/1.6 -apple-system,Segoe UI,Roboto,sans-serif;color:#18181b;white-space:pre-wrap;word-break:break-word">${escaped}</body>`;
}


function EmailPreviewModal({ subject, html, onClose }: { subject: string | null; html: string; onClose: () => void }) {
  return (
    <Modal
      open
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-zinc-400" />
          <span className="truncate">{subject ?? "(no subject)"}</span>
        </span>
      }
      size="xl"
    >
      <iframe
        title={subject ?? "Email preview"}
        srcDoc={html}
        sandbox=""
        className="-mx-5 -my-4 h-[75vh] w-[calc(100%+2.5rem)] bg-white"
      />
    </Modal>
  );
}

/**
 * The line under the message box when she's writing a text.
 *
 * It measures what will ACTUALLY be sent. The send path straightens pasted
 * punctuation first — a curly apostrophe forces an encoding where a text
 * holds 70 characters instead of 160, and nobody can tell the two apart in a
 * message — so counting the raw box would report a cost she is never charged.
 * Nothing to click: the only thing worth her attention is a message that is
 * genuinely too long even after that.
 */
function SmsMeter({ body }: { body: string }) {
  const len = smsLength(tidyForSms(body));
  const tone = len.tooLong ? "text-rose-400"
    : len.parts >= MAX_SMS_PARTS - 2 ? "text-amber-400"
    : "text-zinc-500";
  const shorten = Math.ceil(((len.parts - MAX_SMS_PARTS) * (len.encoding === "UCS-2" ? 67 : 153)) / 10) * 10;

  return (
    <div data-sms-meter className="mt-1.5 flex flex-wrap items-center gap-x-2 text-[10px]">
      <span className={tone} data-sms-parts={len.parts}>
        {len.characters.toLocaleString()} characters · {len.parts} of {MAX_SMS_PARTS} text{len.parts === 1 ? "" : "s"}
      </span>
      {len.tooLong && (
        <span className="text-rose-400">
          · too long to send{len.encoding === "UCS-2" && len.forcedBy.length > 0
            ? ` — ${describeForced(len.forcedBy)} halves what fits in a text`
            : ` — shorten it by about ${shorten} characters, or send it as an email`}
        </span>
      )}
    </div>
  );
}
